import React, { useState, useEffect, useRef } from 'react';
import { Edit, MapPin, Clock, DollarSign, Truck, Save, Loader, CheckCircle } from 'lucide-react';
import { getCurrentTenant, updateTenantProfile } from '../../../../services/api/settingsApi';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';

const CompanySettings = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [formData, setFormData] = useState({
        businessName: '',
        tradeLicenseNo: '',
        businessType: '',
        category: '',
        description: '',
        contactName: '',
        profession: '',
        mobile: '',
        whatsapp: '',
        email: '',
        addressLine: '',
        city: '',
        openingTime: '',
        closingTime: '',
        deliveryOption: false,
        deliveryArea: '',
        minimumOrderValue: '',
        cutoffTime: '',
        autoWriteOffThreshold: '3.00',
        targetCalculationMethod: 'OPERATIONS_LOG'
    });

    // Prevent duplicate API calls in StrictMode
    const isFetchingRef = useRef(false);

    // Fetch tenant data on mount
    useEffect(() => {
        fetchTenantData();
    }, []);

    const fetchTenantData = async () => {
        // Guard against duplicate calls
        if (isFetchingRef.current) {
            console.log('[CompanySettings] Already fetching tenant data, skipping duplicate call');
            return;
        }

        isFetchingRef.current = true;
        console.log('[CompanySettings] Starting to fetch tenant data...');

        try {
            setLoading(true);
            setError(null);
            const tenant = await getCurrentTenant();

            console.log('[CompanySettings] Tenant data received:', tenant);

            // Map backend data to form fields - matching actual response structure
            const mappedData = {
                businessName: tenant.businessName || '',
                tradeLicenseNo: tenant.tradeLicenseNo || '',
                businessType: tenant.businessType || '',
                category: tenant.categoryName || '',
                description: tenant.description || '',
                contactName: tenant.contactName || '',
                profession: tenant.profession || 'Manager',
                mobile: tenant.phone || '', // Backend uses 'phone', not 'mobile'
                whatsapp: tenant.whatsapp || tenant.phone || '',
                email: tenant.email || '',
                addressLine: tenant.addressLine || tenant.address || '', 
                city: tenant.city || 'Dubai',
                openingTime: tenant.openingTime || '8:00 AM',
                closingTime: tenant.closingTime || '7:00 PM',
                deliveryOption: tenant.deliveryOption === 'true' || tenant.deliveryOption === true,
                deliveryArea: 'We deliver all over Dubai',
                minimumOrderValue: tenant.minimumOrderValue?.toString() || '100',
                cutoffTime: tenant.cutoffTime || '5:00 PM',
                autoWriteOffThreshold: tenant.autoWriteOffThreshold?.toString() || '3.00',
                targetCalculationMethod: tenant.targetCalculationMethod || 'OPERATIONS_LOG'
            };

            setFormData(mappedData);
            setOriginalData(mappedData);
        } catch (err) {
            console.error('[CompanySettings] Failed to load tenant data:', err);
            setError('Failed to load company settings. Please try again.');
        } finally {
            setLoading(false);
            // Reset the flag after fetch completes
            isFetchingRef.current = false;
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            // Prepare data for backend
            const updateData = {
                businessName: formData.businessName,
                tradeLicenseNo: formData.tradeLicenseNo,
                businessType: formData.businessType,
                description: formData.description,
                contactName: formData.contactName,
                profession: formData.profession,
                mobile: formData.mobile,
                whatsapp: formData.whatsapp,
                email: formData.email,
                addressLine: formData.addressLine,
                city: formData.city,
                openingTime: formData.openingTime,
                closingTime: formData.closingTime,
                deliveryOption: formData.deliveryOption ? 'true' : 'false',
                minimumOrderValue: parseFloat(formData.minimumOrderValue) || 0,
                cutoffTime: formData.cutoffTime,
                autoWriteOffThreshold: parseFloat(formData.autoWriteOffThreshold) || 3.00,
                targetCalculationMethod: formData.targetCalculationMethod
            };

            await updateTenantProfile(updateData);
            setOriginalData(formData);
            setIsEditing(false);
            setSuccessMessage('Company settings updated successfully!');
            // Auto-clear after 4 seconds
            setTimeout(() => setSuccessMessage(null), 4000);
        } catch (err) {
            console.error('[CompanySettings] Failed to update:', err);
            setError('Failed to update company settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
        setError(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-purple-50 via-blue-50 to-white">
                <LoadingSpinner size="lg" message="Loading company settings..." />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col p-2 md:p-4 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between mb-2 md:mb-3">
                <div>
                    <h2 className="text-lg md:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Company Settings
                    </h2>
                    <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">Manage your company profile and preferences</p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg text-sm md:text-base"
                    >
                        <Edit size={16} />
                        <span className="hidden md:inline">Edit</span>
                    </button>
                )}
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="flex-shrink-0 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg mb-3 text-xs md:text-sm">
                    <CheckCircle size={15} className="shrink-0 text-emerald-500" />
                    {successMessage}
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3 text-xs md:text-sm">
                    {error}
                </div>
            )}

            {/* Scrollable Form Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                {/* 2-Column Grid Layout for Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-2.5">
                    {/* Business Information */}
                    <div className="bg-white rounded-lg border border-gray-200 p-2 md:p-2.5">
                        <h3 className="text-[11px] md:text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1">
                            <div className="w-0.5 h-3.5 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                            Business Information
                        </h3>
                        <div className="space-y-1.5">
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                    Business Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="businessName"
                                    value={formData.businessName}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                    Trade License No. <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="tradeLicenseNo"
                                    value={formData.tradeLicenseNo}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Business Type</label>
                                    <select
                                        name="businessType"
                                        value={formData.businessType}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    >
                                        <option>Grocery Store</option>
                                        <option>Restaurant</option>
                                        <option>Supermarket</option>
                                        <option>Wholesale</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Category</label>
                                    <input
                                        type="text"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    rows="2"
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 resize-none text-xs md:text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white rounded-lg border border-gray-200 p-2 md:p-2.5">
                        <h3 className="text-[11px] md:text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1">
                            <div className="w-0.5 h-3.5 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                            Contact Information
                        </h3>
                        <div className="space-y-1.5">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                        Contact Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="contactName"
                                        value={formData.contactName}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Profession</label>
                                    <select
                                        name="profession"
                                        value={formData.profession}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    >
                                        <option>Manager</option>
                                        <option>Owner</option>
                                        <option>Supervisor</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                        Mobile <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                        WhatsApp <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="whatsapp"
                                        value={formData.whatsapp}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Address */}
                    <div className="bg-white rounded-lg border border-gray-200 p-2 md:p-2.5">
                        <h3 className="text-[11px] md:text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1">
                            <MapPin size={12} className="text-blue-600" />
                            Location
                        </h3>
                        <div className="space-y-1.5">
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Address Line</label>
                                <input
                                    type="text"
                                    name="addressLine"
                                    value={formData.addressLine}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">City</label>
                                <select
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                >
                                    <option>Dubai</option>
                                    <option>Abu Dhabi</option>
                                    <option>Sharjah</option>
                                    <option>Ajman</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Operating Hours & Delivery */}
                    <div className="bg-white rounded-lg border border-gray-200 p-2 md:p-2.5">
                        <h3 className="text-[11px] md:text-xs font-bold text-gray-900 mb-1.5 flex items-center gap-1">
                            <Clock size={12} className="text-purple-600" />
                            Hours & Delivery
                        </h3>
                        <div className="space-y-1.5">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Opening Time</label>
                                    <input
                                        type="text"
                                        name="openingTime"
                                        value={formData.openingTime}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Closing Time</label>
                                    <input
                                        type="text"
                                        name="closingTime"
                                        value={formData.closingTime}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 py-1">
                                <input
                                    type="checkbox"
                                    name="deliveryOption"
                                    checked={formData.deliveryOption}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label className="text-[10px] md:text-xs font-semibold text-gray-700">Enable Delivery</label>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Min. Order (AED)</label>
                                    <input
                                        type="text"
                                        name="minimumOrderValue"
                                        value={formData.minimumOrderValue}
                                        onChange={handleInputChange}
                                        disabled={!isEditing || !formData.deliveryOption}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">Cutoff Time</label>
                                    <input
                                        type="text"
                                        name="cutoffTime"
                                        value={formData.cutoffTime}
                                        onChange={handleInputChange}
                                        disabled={!isEditing || !formData.deliveryOption}
                                        className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                    Auto Write-Off Threshold (AED)
                                </label>
                                <input
                                    type="number"
                                    name="autoWriteOffThreshold"
                                    value={formData.autoWriteOffThreshold}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    step="0.01"
                                    min="0"
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] md:text-xs font-semibold text-gray-700 mb-0.5">
                                    Sales Target Evaluation Method
                                </label>
                                <select
                                    name="targetCalculationMethod"
                                    value={formData.targetCalculationMethod}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className="w-full px-2 py-1 md:px-3 md:py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600 text-xs md:text-sm"
                                >
                                    <option value="OPERATIONS_LOG">Operations Log (Daily Report Transactions)</option>
                                    <option value="DELIVERED_ORDERS">Delivered Orders (Sales Orders Pipeline)</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
                <div className="flex-shrink-0 flex justify-end gap-2 md:gap-3 pt-2 md:pt-3 border-t border-gray-200 mt-2">
                    <button
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-4 md:px-5 py-1.5 md:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold disabled:opacity-50 text-sm md:text-base"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1 md:gap-2 px-4 md:px-5 py-1.5 md:py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 text-sm md:text-base"
                    >
                        {saving ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Save size={16} />
                                <span>Save Changes</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CompanySettings;
